import { loadPyodide, version as pyodide_version, type PyodideInterface } from 'pyodide';

interface PyodidePackageCache {
  pypiPackages: Set<string>;
  standardPackages: Set<string>;
}

interface PyodideExecutionContext {
  explorationId: string;
  cellId: string;
}

class PyodideService {
  private static instance: PyodideService | null = null;
  private pyodide: PyodideInterface | null = null;
  private loading = false;
  private packageCache: PyodidePackageCache = {
    pypiPackages: new Set(),
    standardPackages: new Set(),
  };
  private loadingPromise: Promise<PyodideInterface> | null = null;
  private cellNamespaces: Map<string, any> = new Map();

  private constructor() {}

  static getInstance(): PyodideService {
    if (!PyodideService.instance) {
      PyodideService.instance = new PyodideService();
    }
    return PyodideService.instance;
  }

  async initializePyodide(): Promise<PyodideInterface> {
    if (this.pyodide) {
      return this.pyodide;
    }

    if (this.loading && this.loadingPromise) {
      return this.loadingPromise;
    }

    this.loading = true;
    this.loadingPromise = this.loadPyodideAndModules();

    try {
      this.pyodide = await this.loadingPromise;
      return this.pyodide;
    } finally {
      this.loading = false;
      this.loadingPromise = null;
    }
  }

  private async loadPyodideAndModules(): Promise<PyodideInterface> {
    const pyodide = await loadPyodide({
      indexURL: `https://cdn.jsdelivr.net/pyodide/v${pyodide_version}/full/`,
    });

    // Default packages that are always loaded
    await pyodide.loadPackage(['pandas']);
    this.packageCache.standardPackages.add('pandas');

    return pyodide;
  }

  async ensurePackagesLoaded(
    pyCode: string,
    pypiPackages: string[],
    appendLog: (message: { type: 'out' | 'error'; string: string }) => void,
  ): Promise<void> {
    const pyodide = await this.initializePyodide();

    // Load packages from imports
    await pyodide.loadPackagesFromImports(pyCode);

    // Load pypi packages that haven't been loaded yet
    const newPypiPackages = pypiPackages.filter((pkg) => !this.packageCache.pypiPackages.has(pkg));

    if (newPypiPackages.length > 0) {
      if (!this.packageCache.standardPackages.has('micropip')) {
        await pyodide.loadPackage(['micropip']);
        this.packageCache.standardPackages.add('micropip');
      }

      const micropip = pyodide.pyimport('micropip');

      for (const pypiPackage of newPypiPackages) {
        appendLog({
          type: 'out',
          string: `Installing package: ${pypiPackage}`,
        });

        try {
          await micropip.install(pypiPackage);
          this.packageCache.pypiPackages.add(pypiPackage);
        } catch (error) {
          appendLog({
            type: 'error',
            string: `Failed to install package ${pypiPackage}: ${error}`,
          });
          throw error;
        }
      }
    }

    appendLog({
      type: 'out',
      string: 'Packages loaded',
    });
  }

  async executeCode(
    pyCode: string,
    context: PyodideExecutionContext,
    appendLog: (message: { type: 'out' | 'error'; string: string }) => void,
  ): Promise<any> {
    const pyodide = await this.initializePyodide();
    const { cellId } = context;

    pyodide.setStdout({
      batched: (str) => appendLog({ type: 'out', string: str }),
    });
    pyodide.setStderr({
      batched: (str) => appendLog({ type: 'error', string: str }),
    });

    let result: any;
    try {
      // Get or create an isolated namespace for this specific cell
      let namespace = this.cellNamespaces.get(cellId);

      if (namespace === undefined) {
        namespace = pyodide.runPython(`
          import sys
          import types
          import builtins

          # Create a new namespace with only built-ins and essential components
          cell_namespace = {
            '__name__': '__main__',
            '__doc__': None,
            '__package__': None,
            '__loader__': None,
            '__spec__': None,
            '__builtins__': builtins,
            '__cached__': None,
            '__file__': None,
          }

          # Add sys module for imports to work
          cell_namespace['sys'] = sys

          # Copy all loaded modules from sys.modules so imports work
          # This gives access to all installed packages without user variables
          for module_name, module_obj in sys.modules.items():
            if module_obj is not None:
              # Make the module importable in the new namespace
              # The import system will handle this automatically
              pass

          cell_namespace
        `);

        this.cellNamespaces.set(cellId, namespace);
      }

      // Execute the code in the cell's isolated namespace
      result = await pyodide.runPythonAsync(pyCode, { globals: namespace });

      return { result, namespace };
    } finally {
      pyodide.setStdout({ batched: () => {} });
      pyodide.setStderr({ batched: () => {} });
      // PyProxy cleanup is handled by the caller (convertPythonResult) since the result needs to be processed first
    }
  }

  async registerSupersimpleModule(
    loadPyData: (
      title: string,
      page?: boolean,
      limit?: number,
      page_size?: number,
      columns?: string[],
    ) => Promise<any>,
  ): Promise<void> {
    const pyodide = await this.initializePyodide();

    pyodide.registerJsModule('supersimple', {
      getBlock: (title: string) => {
        return {
          fetchAll: (
            keyword_args:
              | {
                  limit?: number;
                  page_size?: number;
                  columns?: any;
                }
              | undefined,
          ) => {
            const { limit, page_size, columns } = keyword_args ?? {};
            const load = loadPyData(title, false, limit, page_size ?? 2000, columns?.toJs?.());

            return {
              as_json: async () => {
                return pyodide.toPy((await load)?.rows ?? []);
              },
              as_df: async () => {
                const data = await load;

                const columnTypes: { [key: string]: string } = (data?.fields ?? []).reduce(
                  (acc: any, field: any) => ({
                    ...acc,
                    [field.key]: this.convertToPythonType(field.type),
                  }),
                  {},
                );

                const columnKeyNames: { [key: string]: string } = (data?.fields ?? []).reduce(
                  (acc: any, field: any) => ({ ...acc, [field.key]: field.name }),
                  {},
                );

                return pyodide
                  .pyimport('pandas')
                  .DataFrame.callKwargs({ data: pyodide.toPy(data?.rows ?? []) })
                  .astype.callKwargs({ dtype: pyodide.toPy(columnTypes) })
                  .rename.callKwargs({ columns: pyodide.toPy(columnKeyNames) });
              },
            };
          },
        };
      },
    });
  }

  async unregisterSupersimpleModule(): Promise<void> {
    const pyodide = await this.initializePyodide();
    pyodide.unregisterJsModule('supersimple');
  }

  private convertToPythonType(type: string): string {
    switch (type) {
      case 'Integer':
        return 'int64';
      case 'Float':
        return 'float64';
      case 'Boolean':
        return 'bool';
      case 'DateTime':
        return 'datetime64[ns]';
      default:
        return 'object';
    }
  }

  reset(): void {
    // Clean up all cell namespaces
    this.cellNamespaces.forEach((namespace) => {
      if (namespace !== undefined && typeof namespace.destroy === 'function') {
        namespace.destroy();
      }
    });
    this.cellNamespaces.clear();

    this.pyodide = null;
    this.loading = false;
    this.packageCache = {
      pypiPackages: new Set(),
      standardPackages: new Set(),
    };
    this.loadingPromise = null;
  }

  // Method to clean up a specific cell's namespace (useful when a cell is deleted)
  clearCellNamespace(cellId: string): void {
    const namespace = this.cellNamespaces.get(cellId);
    if (namespace !== undefined && typeof namespace.destroy === 'function') {
      namespace.destroy();
    }
    this.cellNamespaces.delete(cellId);
  }

  getPackageCache(): PyodidePackageCache {
    return {
      pypiPackages: new Set(this.packageCache.pypiPackages),
      standardPackages: new Set(this.packageCache.standardPackages),
    };
  }
}

export const pyodideService = PyodideService.getInstance();
